import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { Close as CloseIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Loader } from "../../components";
import { FileUploaderField, SubmitButton, TextField } from "../../components/formik";
import { types } from "../../ducks";
import {
  createExhibitorDocument,
  deleteDocument,
  getDocument,
  updateDocument,
  updateDocumentPdf
} from "../../ducks/actions";
import useActionRequest from "../../hooks/useActionRequest";

const useStyles = makeStyles(theme => ({
  title: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3)
  },
  uploaderContainer: {
    display: "flex",
    justifyContent: "center"
  },
  deleteButton: {
    color: "#FF0033",
    marginRight: "auto"
  }
}));

const DocumentDialog = ({ render, exhibitorId }) => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [id, setId] = useState(false);

  const createDocumentRequest = useActionRequest({
    action: createExhibitorDocument
  });
  const updateDocumentRequest = useActionRequest({
    action: updateDocument
  });
  const updateDocumentPdfRequest = useActionRequest({
    action: updateDocumentPdf
  });

  const onSubmit = async ({ pdf, ...document }) => {
    if (id) {
      updateDocumentRequest({ id, document });
      updateDocumentPdfRequest({ documentId: id, pdf });
    } else {
      const {
        data: {
          data: { id: documentId }
        }
      } = await createDocumentRequest({ exhibitorId, document });
      updateDocumentPdfRequest({ documentId, pdf });
    }
    setOpen(false);
  };

  const deleteDocumentRequest = useActionRequest({
    action: deleteDocument
  });

  const onDeleteDocument = () => {
    deleteDocumentRequest({ id });
    setOpen(false);
  };

  useEffect(() => {
    if (id) {
      dispatch(getDocument({ id }));
    }
  }, [id, dispatch]);

  const {
    data: { data: document },
    documentLoading
  } = useQuery({ type: types.GET_DOCUMENT, defaultData: { data: {} } });

  const onOpen = ({ id }) => {
    setOpen(true);
    setId(id);
  };

  const onClose = () => {
    setOpen(false);
  };

  return (
    <>
      {render({ onOpen })}
      <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
        <Typography variant="h5" className={classes.title}>
          <strong>
            {intl.formatMessage({
              id: "Exhibitors.DocumentDialog.EditDocument",
              defaultMessage: "Edit Document"
            })}
          </strong>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Typography>
        <Loader loading={documentLoading}>
          <Formik
            initialValues={id ? document : { title: "", description: "" }}
            enableReinitialize
            onSubmit={onSubmit}
          >
            <Form>
              <DialogContent>
                <FileUploaderField
                  name="pdf"
                  accept="application/pdf"
                  label={intl.formatMessage({
                    id: "Exhibitors.DocumentDialog.PDFFile",
                    defaultMessage: "PDF file"
                  })}
                  buttonLabel={intl.formatMessage({
                    id: "Exhibitors.DocumentDialog.Upload",
                    defaultMessage: "Upload"
                  })}
                />
                <TextField
                  name="title"
                  variant="outlined"
                  label={intl.formatMessage({
                    id: "Exhibitors.DocumentDialog.Title",
                    defaultMessage: "Title"
                  })}
                  fullWidth
                />
                <TextField
                  name="description"
                  variant="outlined"
                  label={intl.formatMessage({
                    id: "Exhibitors.DocumentDialog.Description",
                    defaultMessage: "Description"
                  })}
                  multiline
                  rows={3}
                  fullWidth
                />
              </DialogContent>
              <DialogActions>
                {id && (
                  <Button onClick={onDeleteDocument} className={classes.deleteButton} size="small">
                    <DeleteIcon fontSize="small" />
                    &nbsp;&nbsp;&nbsp;
                    {intl.formatMessage({
                      id: "Exhibitors.DocumentDialog.Delete",
                      defaultMessage: "Delete"
                    })}
                  </Button>
                )}
                <Button color="primary" onClick={onClose} size="small">
                  {intl.formatMessage({
                    id: "Exhibitors.DocumentDialog.Cancel",
                    defaultMessage: "Cancel"
                  })}
                </Button>
                <SubmitButton color="primary" variant="contained" size="small">
                  {intl.formatMessage({
                    id: "Exhibitors.DocumentDialog.Save",
                    defaultMessage: "Save"
                  })}
                </SubmitButton>
              </DialogActions>
            </Form>
          </Formik>
        </Loader>
      </Dialog>
    </>
  );
};

export default DocumentDialog;
